import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAuth } from '~/app/auth/context/AuthContext';
import { MaintenanceExecutionService } from '~/app/services/maintenance-execution/maintenance-execution.service';
import { UserService } from '~/app/services/user/user.service';
import {
  asOptionalString,
  coerceOptionalString,
} from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

export function useTicketDetailPage() {
  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const query = useQuery({
    queryKey: ['maintenance-execution', 'ticket', ticketId, organizationId],
    queryFn: () =>
      MaintenanceExecutionService.getTicket(ticketId, organizationId),
    enabled: Boolean(ticketId && organizationId),
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'org-lookup', organizationId],
    queryFn: () => UserService.listOrganizationUsers(organizationId),
    enabled: Boolean(organizationId),
    staleTime: 60_000,
  });

  const events = useMemo(
    () =>
      [...(query.data?.events ?? [])].sort(
        (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
      ),
    [query.data?.events],
  );

  const actorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const orgUser of usersQuery.data ?? []) {
      map.set(orgUser.id, orgUser.name);
    }
    return map;
  }, [usersQuery.data]);

  return {
    ticketId,
    ticket: query.data
      ? {
          id: query.data.id,
          code: query.data.code,
          type: query.data.type,
          status: query.data.status,
          needsFollowUp: query.data.needsFollowUp,
          priority: query.data.priority,
          title: query.data.title,
          description: asOptionalString(query.data.description),
          siteId: asOptionalString(query.data.siteId),
          assetId: asOptionalString(query.data.assetId),
          taskId: asOptionalString(query.data.taskId),
          preventivePlanId: asOptionalString(query.data.preventivePlanId),
          planCode: asOptionalString(query.data.planCode),
          planName: asOptionalString(query.data.planName),
          dueAt: query.data.dueAt ?? null,
          subtaskNames: query.data.subtaskNames ?? [],
          activeWorkOrderId: asOptionalString(query.data.activeWorkOrderId),
          activeWorkOrderCode: asOptionalString(query.data.activeWorkOrderCode),
          activeWorkOrderStatus: asOptionalString(
            query.data.activeWorkOrderStatus,
          ),
          activeOnHoldReason: asOptionalString(query.data.activeOnHoldReason),
          incidentId: coerceOptionalString(query.data.incidentId),
          incidentCode: coerceOptionalString(query.data.incidentCode),
          incidentTitle: coerceOptionalString(query.data.incidentTitle),
        }
      : null,
    events,
    visits: query.data?.visits ?? [],
    actorNameById,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
